import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '../components/ui/dialog';
import {
  Sparkles, Wrench, ArrowUpCircle, Bug, Plus, MessageSquarePlus,
  ChevronDown, ChevronUp, Send, Clock, CheckCircle2, AlertCircle, X, Trash2, Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';

const TYPE_CONFIG = {
  feature: { label: 'New Feature', icon: Sparkles, color: 'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-700' },
  fix: { label: 'Bug Fix', icon: Bug, color: 'bg-red-100 text-red-700 border-red-300 dark:bg-red-900/30 dark:text-red-400 dark:border-red-700' },
  update: { label: 'Update', icon: ArrowUpCircle, color: 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-700' },
  improvement: { label: 'Improvement', icon: Wrench, color: 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-700' },
};

const FEEDBACK_TYPES = [
  { value: 'change_request', label: 'Request a Change' },
  { value: 'edit_request', label: 'Request an Edit' },
  { value: 'question', label: 'Question / How to use' },
];

function EntryTypeBadge({ type }) {
  const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.update;
  const Icon = cfg.icon;
  return (
    <Badge variant="outline" className={`text-xs ${cfg.color}`}>
      <Icon className="h-3 w-3 mr-1" />{cfg.label}
    </Badge>
  );
}

function ChangelogEntry({ entry, isAdmin, onFeedback, onDelete, onEdit }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card className="transition-all hover:shadow-sm" data-testid={`changelog-${entry.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
              <EntryTypeBadge type={entry.entry_type} />
              {entry.version && <span className="text-[11px] font-mono text-muted-foreground">v{entry.version}</span>}
              <span className="text-[11px] text-muted-foreground">{new Date(entry.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
            </div>
            <h3 className="text-base font-semibold leading-tight">{entry.title}</h3>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{entry.description}</p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {isAdmin && (
              <>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(entry)} data-testid={`edit-changelog-${entry.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => onDelete(entry.id)} data-testid={`delete-changelog-${entry.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </>
            )}
          </div>
        </div>

        {entry.how_to_use && (
          <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-1 mt-2.5 text-xs font-medium text-primary hover:underline">
            {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            {expanded ? 'Hide guide' : 'How to use'}
          </button>
        )}
        {expanded && entry.how_to_use && (
          <div className="mt-2 p-3 rounded-md bg-muted/50 text-sm leading-relaxed">
            {entry.how_to_use}
          </div>
        )}

        <div className="mt-3 pt-2.5 border-t flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => onFeedback(entry.id, 'change_request')} data-testid={`request-change-${entry.id}`}>
            <MessageSquarePlus className="h-3 w-3 mr-1" />Request a Change
          </Button>
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => onFeedback(entry.id, 'question')} data-testid={`ask-question-${entry.id}`}>
            <AlertCircle className="h-3 w-3 mr-1" />Ask a Question
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function FeedbackDialog({ open, onClose, changelogId, feedbackType, onSubmit }) {
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedType, setSelectedType] = useState(feedbackType || 'change_request');

  useEffect(() => { if (feedbackType) setSelectedType(feedbackType); }, [feedbackType]);

  const handleSubmit = async () => {
    if (!message.trim()) { toast.error('Please enter a message'); return; }
    setSubmitting(true);
    try {
      await onSubmit({ changelog_id: changelogId, feedback_type: selectedType, message: message.trim() });
      setMessage('');
      onClose();
      toast.success('Feedback submitted');
    } catch (e) {
      toast.error('Failed to submit');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Submit Feedback</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Select value={selectedType} onValueChange={setSelectedType}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="change_request">Request a Change</SelectItem>
              <SelectItem value="edit_request">Request an Edit</SelectItem>
              <SelectItem value="question">Question / How to use</SelectItem>
            </SelectContent>
          </Select>
          <Textarea placeholder="Describe your request or question..." value={message} onChange={e => setMessage(e.target.value)} rows={4} data-testid="feedback-message" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="submit-feedback">
            <Send className="h-3.5 w-3.5 mr-1.5" />{submitting ? 'Sending...' : 'Submit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdminEntryDialog({ open, onClose, editEntry, onSave }) {
  const [form, setForm] = useState({ title: '', description: '', entry_type: 'feature', how_to_use: '', version: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editEntry) {
      setForm({
        title: editEntry.title || '',
        description: editEntry.description || '',
        entry_type: editEntry.entry_type || 'feature',
        how_to_use: editEntry.how_to_use || '',
        version: editEntry.version || '',
      });
    } else {
      setForm({ title: '', description: '', entry_type: 'feature', how_to_use: '', version: '' });
    }
  }, [editEntry, open]);

  const handleSave = async () => {
    if (!form.title.trim() || !form.description.trim()) { toast.error('Title and description required'); return; }
    setSaving(true);
    try {
      await onSave(form, editEntry ? editEntry.id : null);
      onClose();
    } catch (e) {
      toast.error('Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{editEntry ? 'Edit Entry' : 'Add Changelog Entry'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Select value={form.entry_type} onValueChange={v => setForm({ ...form, entry_type: v })}>
              <SelectTrigger data-testid="entry-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="feature">New Feature</SelectItem>
                <SelectItem value="fix">Bug Fix</SelectItem>
                <SelectItem value="update">Update</SelectItem>
                <SelectItem value="improvement">Improvement</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder="Version (e.g. 2.5)" value={form.version} onChange={e => setForm({ ...form, version: e.target.value })} data-testid="entry-version" />
          </div>
          <Input placeholder="Title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="entry-title" />
          <Textarea placeholder="Description — what changed and why" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} data-testid="entry-description" />
          <Textarea placeholder="How to use (optional) — step-by-step guide for users" value={form.how_to_use} onChange={e => setForm({ ...form, how_to_use: e.target.value })} rows={3} data-testid="entry-how-to-use" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-entry-btn">
            {saving ? 'Saving...' : editEntry ? 'Update' : 'Publish'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FeedbackList({ feedbacks, onStatusChange }) {
  if (feedbacks.length === 0) return <p className="text-sm text-muted-foreground p-4">No feedback yet.</p>;

  const statusColors = {
    open: 'bg-amber-100 text-amber-700',
    in_progress: 'bg-blue-100 text-blue-700',
    resolved: 'bg-emerald-100 text-emerald-700',
    dismissed: 'bg-muted text-muted-foreground',
  };

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Type</TableHead>
            <TableHead>Message</TableHead>
            <TableHead>User</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {feedbacks.map(f => (
            <TableRow key={f.id}>
              <TableCell><Badge variant="outline" className="text-xs">{f.feedback_type.replace('_', ' ')}</Badge></TableCell>
              <TableCell className="text-sm max-w-xs truncate">{f.message}</TableCell>
              <TableCell className="text-sm">{f.user_name}</TableCell>
              <TableCell className="text-xs text-muted-foreground">{new Date(f.created_at).toLocaleDateString()}</TableCell>
              <TableCell>
                <Select value={f.status} onValueChange={v => onStatusChange(f.id, v)}>
                  <SelectTrigger className="h-7 text-xs w-28"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="dismissed">Dismissed</SelectItem>
                  </SelectContent>
                </Select>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function ChangelogPage() {
  const { isAdminLevel } = useAuth();
  const [entries, setEntries] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('changelog');
  const [filterType, setFilterType] = useState('all');
  // Dialogs
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackTarget, setFeedbackTarget] = useState({ id: null, type: null });
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [editEntry, setEditEntry] = useState(null);

  const loadEntries = useCallback(async () => {
    try {
      const res = await api.get('/changelog');
      setEntries(res.data);
      // Mark as seen
      api.post('/changelog/mark-seen').catch(() => {});
    } catch (e) { /* silent */ }
    setLoading(false);
  }, []);

  const loadFeedbacks = useCallback(async () => {
    try {
      const res = await api.get('/changelog/feedback/all');
      setFeedbacks(res.data);
    } catch (e) { /* only admins */ }
  }, []);

  useEffect(() => { loadEntries(); }, [loadEntries]);
  useEffect(() => { if (activeTab === 'feedback' && isAdminLevel()) loadFeedbacks(); }, [activeTab, isAdminLevel, loadFeedbacks]);

  const openFeedbackDialog = (changelogId, type) => {
    setFeedbackTarget({ id: changelogId, type });
    setFeedbackOpen(true);
  };

  const handleFeedbackSubmit = async (data) => {
    await api.post('/changelog/feedback', data);
  };

  const handleSaveEntry = async (form, entryId) => {
    if (entryId) {
      await api.put('/changelog/' + entryId, form);
      toast.success('Entry updated');
    } else {
      await api.post('/changelog', form);
      toast.success('Entry published');
    }
    loadEntries();
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Delete this changelog entry?')) return;
    await api.delete('/changelog/' + entryId);
    toast.success('Entry deleted');
    loadEntries();
  };

  const handleEditEntry = (entry) => {
    setEditEntry(entry);
    setEntryDialogOpen(true);
  };

  const handleStatusChange = async (feedbackId, status) => {
    await api.put('/changelog/feedback/' + feedbackId + '/status', { status });
    loadFeedbacks();
  };

  const filteredEntries = filterType === 'all' ? entries : entries.filter(e => e.entry_type === filterType);

  const featureCount = entries.filter(e => e.entry_type === 'feature').length;
  const fixCount = entries.filter(e => e.entry_type === 'fix').length;
  const updateCount = entries.filter(e => e.entry_type === 'update').length;
  const improvementCount = entries.filter(e => e.entry_type === 'improvement').length;

  return (
    <DashboardLayout>
      <div data-testid="changelog-page" className="space-y-6 max-w-4xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">What's New</h1>
            <p className="text-muted-foreground">Latest features, updates, and fixes</p>
          </div>
          {isAdminLevel() && (
            <Button onClick={() => { setEditEntry(null); setEntryDialogOpen(true); }} data-testid="add-entry-btn">
              <Plus className="h-4 w-4 mr-1.5" />Add Entry
            </Button>
          )}
        </div>

        {/* Tabs for admin */}
        {isAdminLevel() && (
          <div className="flex gap-1 border-b">
            <button onClick={() => setActiveTab('changelog')} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'changelog' ? 'border-slate-900 text-slate-900 dark:border-white dark:text-white' : 'border-transparent text-muted-foreground hover:text-foreground'}`} data-testid="tab-changelog">
              Changelog
            </button>
            <button onClick={() => setActiveTab('feedback')} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'feedback' ? 'border-slate-900 text-slate-900 dark:border-white dark:text-white' : 'border-transparent text-muted-foreground hover:text-foreground'}`} data-testid="tab-feedback">
              User Feedback {feedbacks.length > 0 && <Badge className="ml-1 text-[10px] h-4 min-w-4 px-1">{feedbacks.filter(f => f.status === 'open').length}</Badge>}
            </button>
          </div>
        )}

        {activeTab === 'changelog' && (
          <>
            {/* Filter chips */}
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={() => setFilterType('all')} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${filterType === 'all' ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' : 'bg-muted text-muted-foreground hover:text-foreground'}`}>
                All ({entries.length})
              </button>
              <button onClick={() => setFilterType('feature')} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${filterType === 'feature' ? 'bg-emerald-600 text-white' : 'bg-muted text-muted-foreground hover:text-foreground'}`}>
                Features ({featureCount})
              </button>
              <button onClick={() => setFilterType('improvement')} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${filterType === 'improvement' ? 'bg-amber-600 text-white' : 'bg-muted text-muted-foreground hover:text-foreground'}`}>
                Improvements ({improvementCount})
              </button>
              <button onClick={() => setFilterType('fix')} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${filterType === 'fix' ? 'bg-red-600 text-white' : 'bg-muted text-muted-foreground hover:text-foreground'}`}>
                Fixes ({fixCount})
              </button>
              <button onClick={() => setFilterType('update')} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${filterType === 'update' ? 'bg-blue-600 text-white' : 'bg-muted text-muted-foreground hover:text-foreground'}`}>
                Updates ({updateCount})
              </button>
            </div>

            {loading ? (
              <Card><CardContent className="p-8 text-center text-muted-foreground">Loading...</CardContent></Card>
            ) : filteredEntries.length === 0 ? (
              <Card><CardContent className="p-8 text-center text-muted-foreground">No entries found.</CardContent></Card>
            ) : (
              <div className="space-y-3">
                {filteredEntries.map(entry => (
                  <ChangelogEntry
                    key={entry.id}
                    entry={entry}
                    isAdmin={isAdminLevel()}
                    onFeedback={openFeedbackDialog}
                    onDelete={handleDeleteEntry}
                    onEdit={handleEditEntry}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'feedback' && isAdminLevel() && (
          <Card>
            <CardHeader><CardTitle className="text-lg">User Feedback & Requests</CardTitle></CardHeader>
            <CardContent className="p-0">
              <FeedbackList feedbacks={feedbacks} onStatusChange={handleStatusChange} />
            </CardContent>
          </Card>
        )}
      </div>

      <FeedbackDialog
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        changelogId={feedbackTarget.id}
        feedbackType={feedbackTarget.type}
        onSubmit={handleFeedbackSubmit}
      />

      <AdminEntryDialog
        open={entryDialogOpen}
        onClose={() => { setEntryDialogOpen(false); setEditEntry(null); }}
        editEntry={editEntry}
        onSave={handleSaveEntry}
      />
    </DashboardLayout>
  );
}
